import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  IconButton,
  Divider,
  Autocomplete,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  Chip,
  Avatar,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  CheckCircle,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { apiService, Item, Customer, Team } from '../../services/apiService';

const AddOrder: React.FC = () => {
  const navigate = useNavigate();
  const { customerId } = useParams<{ customerId?: string }>();
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<Item[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [customerLocations, setCustomerLocations] = useState<any[]>([]);
  const [selectedEmployees, setSelectedEmployees] = useState<number[]>([]);
  const [nextTicketNumber, setNextTicketNumber] = useState<string>('');
  const [formData, setFormData] = useState({
    teckit_no: '',
    item_name: '',
    campaign_name: '',
    voucher_name: '',
    voucher_number: '',
    period: '',
    unit_price: '',
    quantity: '',
    created_by: '',
    creation_date: '',
    coordinated_by: '',
    coordinated_date: '',
    feedback_by: '',
    feedback_date: '',
    effective_start_date: '',
    effective_end_date: '',
    followed_by: '',
    follow_status: '',
    collection_date: '',
    collection_time: '',
    notes: '',
    status: 'pending',
    customer_id: ''
  });

  // Fetch data on component mount
  useEffect(() => {
    fetchData();
  }, [customerId]);

  const fetchData = async () => {
    try {
      // Fetch items, customers, orders, and teams
      const [itemsResponse, customersResponse, ordersResponse, teamsResponse] = await Promise.all([
        apiService.getItems(),
        apiService.getCustomers(),
        apiService.getOrders(),
        apiService.getTeams()
      ]);

      setItems(itemsResponse.data || []);
      setCustomers(customersResponse.data || []);

      // Auto-select customer if customerId is provided
      if (customerId) {
        const customer = customersResponse.data.find((c: Customer) => c.id.toString() === customerId);
        if (customer) {
          setFormData(prev => ({
            ...prev,
            customer_id: customerId
          }));
          
          // Fetch customer locations
          try {
            const locationsResponse = await apiService.getCustomerLocations(parseInt(customerId));
            const locations = locationsResponse.data || [];
            setCustomerLocations(locations);
            
            // Find teams with matching locations
            const teamsData = teamsResponse.data || [];
            const customerLocationIds = locations.map(l => l.location_id);
            
            const matchingTeams = teamsData.filter((team: Team) => {
              return team.members?.some(member => 
                customerLocationIds.includes(member.location_id)
              );
            });
            
            setTeams(matchingTeams);
          } catch (err) {
            console.error('Failed to fetch customer locations:', err);
          }
        }
      }

      // Generate next ticket number
      const orders = ordersResponse.data || [];
      const maxTicketNumber = orders.reduce((max: number, order: any) => {
        // Extract number from ticket format like "TK001" or "001"
        const ticketNo = order.teckit_no || '0';
        let ticketNum = 0;
        
        if (ticketNo.includes('TK')) {
          // Extract number from TK001 format
          ticketNum = parseInt(ticketNo.replace('TK', '')) || 0;
        } else {
          // Handle legacy format or plain numbers
          ticketNum = parseInt(ticketNo) || 0;
        }
        
        return ticketNum > max ? ticketNum : max;
      }, 0);

      const nextNumber = (maxTicketNumber + 1).toString().padStart(3, '0');
      const formattedTicketNumber = `TK${nextNumber}`;
      setNextTicketNumber(formattedTicketNumber);
      
      // Set the ticket number in form data
      setFormData(prev => ({
        ...prev,
        teckit_no: formattedTicketNumber
      }));
    } catch (error: any) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleItemChange = (item: Item | null) => {
    if (item) {
      setFormData(prev => ({
        ...prev,
        item_name: item.name,
        unit_price: item.unit_price.toString()
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        item_name: '',
        unit_price: ''
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      
      // Convert string values to appropriate types
      const submitData = {
        ...formData,
        unit_price: formData.unit_price ? parseFloat(formData.unit_price) : undefined,
        quantity: formData.quantity ? parseInt(formData.quantity) : undefined,
        status: formData.status as 'pending' | 'in_progress' | 'completed' | 'cancelled',
        customer_id: formData.customer_id ? parseInt(formData.customer_id) : undefined,
        collection_time: formData.collection_time ? `${formData.collection_time}:00` : undefined,
        assigned_employees: selectedEmployees // Include selected employees
      };

      await apiService.createOrder(submitData);
      toast.success('Order created successfully!');
      navigate(customerId ? `/customers/${customerId}` : '/orders');
    } catch (error: any) {
      toast.error(error.message || 'Failed to create order');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(customerId ? `/customers/${customerId}` : '/orders');
  };

  const handleEmployeeSelect = (userId: number) => {
    setSelectedEmployees(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  // Get all employees from matching teams
  const getAllAvailableEmployees = () => {
    const employees: any[] = [];
    teams.forEach(team => {
      if (team.members) {
        team.members.forEach(member => {
          if (member.user) {
            employees.push({
              id: member.user.id,
              name: member.user.name,
              email: member.user.email
            });
          }
        });
      }
    });
    return employees;
  };

  return (
    <Box sx={{ backgroundColor: '#f0f9ff', minHeight: '100vh', p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={handleCancel} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1f2937', fontSize: '2rem' }}>
          Create New Order
        </Typography>
      </Box>

      <Card sx={{ 
        boxShadow: '0 2px 12px rgba(0,0,0,0.08)', 
        borderRadius: '16px',
        backgroundColor: 'white',
        border: 'none'
      }}>
        <CardContent sx={{ p: 4 }}>
          <form onSubmit={handleSubmit}>
            {/* Item Details Section */}
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1f2937', mb: 3 }}>
              Item Details
            </Typography>
            
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Ticket No."
                  value={formData.teckit_no}
                  InputProps={{
                    readOnly: true,
                  }}
                  variant="outlined"
                  sx={{
                    '& .MuiInputBase-input': {
                      backgroundColor: '#f5f5f5',
                    }
                  }}
                />
              </Grid>
              {!customerId && (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Autocomplete
                    options={customers}
                    getOptionLabel={(customer) => `${customer.company_name} - ${customer.contact_person}`}
                    value={customers.find(c => c.id.toString() === formData.customer_id) || null}
                    onChange={(event, newValue) => {
                      handleInputChange('customer_id', newValue?.id?.toString() || '');
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Customer"
                        placeholder="Select Customer"
                        variant="outlined"
                        required
                      />
                    )}
                  />
                </Grid>
              )}
              {customerId && (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Customer"
                    value={customers.find(c => c.id.toString() === customerId)?.name || 'Loading...'}
                    InputProps={{
                      readOnly: true,
                    }}
                    variant="outlined"
                    sx={{
                      '& .MuiInputBase-input': {
                        backgroundColor: '#f5f5f5',
                      }
                    }}
                  />
                </Grid>
              )}
              <Grid size={{ xs: 12, sm: 6 }}>
                <Autocomplete
                  options={items}
                  getOptionLabel={(item) => `${item.name} - ${item.item_code}`}
                  value={items.find(i => i.name === formData.item_name) || null}
                  onChange={(event, newValue) => {
                    handleItemChange(newValue);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Item"
                      placeholder="Select Item"
                      variant="outlined"
                      required
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Unit Price"
                  value={formData.unit_price}
                  InputProps={{
                    readOnly: true,
                  }}
                  variant="outlined"
                  sx={{
                    '& .MuiInputBase-input': {
                      backgroundColor: '#f5f5f5',
                    }
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Campaign Name"
                  value={formData.campaign_name}
                  onChange={(e) => handleInputChange('campaign_name', e.target.value)}
                  placeholder="Enter"
                  variant="outlined"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Voucher Name"
                  value={formData.voucher_name}
                  onChange={(e) => handleInputChange('voucher_name', e.target.value)}
                  placeholder="Enter"
                  variant="outlined"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Voucher Number"
                  value={formData.voucher_number}
                  onChange={(e) => handleInputChange('voucher_number', e.target.value)}
                  placeholder="Enter"
                  variant="outlined"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Period"
                  value={formData.period}
                  onChange={(e) => handleInputChange('period', e.target.value)}
                  placeholder="Enter"
                  variant="outlined"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Unit Price"
                  type="number"
                  value={formData.unit_price}
                  onChange={(e) => handleInputChange('unit_price', e.target.value)}
                  placeholder="Enter"
                  variant="outlined"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Quantity"
                  type="number"
                  value={formData.quantity}
                  onChange={(e) => handleInputChange('quantity', e.target.value)}
                  placeholder="Enter"
                  variant="outlined"
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* Employee Assignment Section (only show if customerId is provided and teams exist) */}
            {customerId && teams.length > 0 && (
              <>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1f2937', mb: 3 }}>
                  Select Employees to Assign
                </Typography>
                <Typography variant="body2" sx={{ mb: 3, color: '#64748b' }}>
                  Employees from teams matching customer locations
                </Typography>
                
                <Box sx={{ border: '1px solid #e5e7eb', borderRadius: '8px', mb: 4 }}>
                  <List sx={{ p: 0 }}>
                    {getAllAvailableEmployees().map((employee, index) => (
                      <ListItem
                        key={employee.id}
                        sx={{
                          borderBottom: index < getAllAvailableEmployees().length - 1 ? '1px solid #e5e7eb' : 'none',
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: '#f9fafb'
                          },
                          backgroundColor: selectedEmployees.includes(employee.id) ? '#f0f9ff' : 'white'
                        }}
                        onClick={() => handleEmployeeSelect(employee.id)}
                      >
                        <Checkbox
                          checked={selectedEmployees.includes(employee.id)}
                          sx={{ mr: 2 }}
                        />
                        <ListItemText
                          primary={employee.name}
                          secondary={employee.email}
                          primaryTypographyProps={{
                            fontWeight: 600
                          }}
                          secondaryTypographyProps={{
                            color: '#6b7280'
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
                <Divider sx={{ my: 3 }} />
              </>
            )}

            {/* Creator Details Section */}
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1f2937', mb: 3 }}>
              Creator Details
            </Typography>
            
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Created By"
                  value={formData.created_by}
                  onChange={(e) => handleInputChange('created_by', e.target.value)}
                  placeholder="Enter"
                  variant="outlined"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Creation Date"
                  type="date"
                  value={formData.creation_date}
                  onChange={(e) => handleInputChange('creation_date', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  variant="outlined"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Coordinated By"
                  value={formData.coordinated_by}
                  onChange={(e) => handleInputChange('coordinated_by', e.target.value)}
                  placeholder="Enter"
                  variant="outlined"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Coordinated Date"
                  type="date"
                  value={formData.coordinated_date}
                  onChange={(e) => handleInputChange('coordinated_date', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  variant="outlined"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Feedback By"
                  value={formData.feedback_by}
                  onChange={(e) => handleInputChange('feedback_by', e.target.value)}
                  placeholder="Enter"
                  variant="outlined"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Feedback Date"
                  type="date"
                  value={formData.feedback_date}
                  onChange={(e) => handleInputChange('feedback_date', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  variant="outlined"
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* Follow-Up Section */}
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1f2937', mb: 3 }}>
              Follow-Up
            </Typography>
            
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Effective Start Date"
                  type="date"
                  value={formData.effective_start_date}
                  onChange={(e) => handleInputChange('effective_start_date', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  variant="outlined"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Effective End Date"
                  type="date"
                  value={formData.effective_end_date}
                  onChange={(e) => handleInputChange('effective_end_date', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  variant="outlined"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Followed By"
                  value={formData.followed_by}
                  onChange={(e) => handleInputChange('followed_by', e.target.value)}
                  placeholder="Enter"
                  variant="outlined"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Follow Status"
                  value={formData.follow_status}
                  onChange={(e) => handleInputChange('follow_status', e.target.value)}
                  placeholder="Enter"
                  variant="outlined"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Collection Date"
                  type="date"
                  value={formData.collection_date}
                  onChange={(e) => handleInputChange('collection_date', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  variant="outlined"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Collection Time"
                  type="time"
                  value={formData.collection_time}
                  onChange={(e) => handleInputChange('collection_time', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  variant="outlined"
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Notes"
                  multiline
                  rows={3}
                  value={formData.notes}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  placeholder="Enter notes..."
                  variant="outlined"
                />
              </Grid>
            </Grid>

            {/* Form Actions */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              pt: 3,
              borderTop: '1px solid #e2e8f0'
            }}>
              <Button
                onClick={handleCancel}
                variant="outlined"
                sx={{
                  px: 4,
                  py: 1.5,
                  borderRadius: '12px',
                  textTransform: 'none',
                  fontWeight: 500,
                  borderColor: '#e2e8f0',
                  color: '#64748b',
                  '&:hover': {
                    borderColor: '#cbd5e1',
                    backgroundColor: '#f8fafc',
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                startIcon={<SaveIcon />}
                sx={{
                  px: 4,
                  py: 1.5,
                  borderRadius: '12px',
                  backgroundColor: '#4A90E2',
                  color: 'white',
                  fontWeight: 500,
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: '#3b82f6',
                  },
                }}
              >
                {loading ? 'Creating...' : 'Create Order'}
              </Button>
            </Box>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default AddOrder;
